import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { IState } from 'app/shared/models/state';
import {
  getCurrentWebhookSelector,
  getMsisdnsForWebhooksSelector,
  getWebhookTypesSelector
} from 'app/webhooks/webhooks.reducer';
import { WebhookSubscription, WebhookType } from 'app/webhooks/model/webhooks.model';
import { Observable } from 'rxjs';
import { Msisdn } from 'app/shared/msisdn/msisdns';
import { Router } from '@angular/router';
import { SaveWebhookAction, UpdateWebhookAction } from 'app/webhooks/webhooks.actions';
import updateFormGroupFromJson from 'app/shared/utils/updateFormGroupFromJson';
import { markFormAsTouched } from 'app/lead-generation/shared/reactive-forms/forEachElementInForm';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'cc-webhook-form',
  templateUrl: './webhook-form.component.html',
  styleUrls: ['./webhook-form.component.scss']
})
export class WebhookFormComponent implements OnInit {
  public webhookForm: FormGroup;
  public eventTypesToSelect: { value: string; name: string }[];
  public allEventTypes: WebhookType[];
  public msisdns$: Observable<Msisdn[]>;
  public existingId: number;

  constructor(
    private fb: FormBuilder,
    private store: Store<IState>,
    private router: Router,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.webhookForm = this.fb.group({
      subjectType: this.fb.control('PROJECT'),
      entityId: this.fb.control(''),
      types: this.fb.control([], [Validators.required]),
      name: this.fb.control('', [Validators.required]),
      endpoint: this.fb.control('', [Validators.required, Validators.pattern(/.*?\..{2,}/)]),
      active: this.fb.control(true)
    });

    this.store.select(getWebhookTypesSelector).subscribe(types => {
      this.allEventTypes = types;
      this.refreshEventTypesToSelect();
    });

    this.store.select(getCurrentWebhookSelector).subscribe(curr => {
      if (curr) {
        this.handleExistingWebhook(curr);
      }
    });

    this.msisdns$ = this.store.select(getMsisdnsForWebhooksSelector);
  }

  public get selectedType(): string {
    return this.webhookForm.get('subjectType').value;
  }

  public canSelectMsisdn(): boolean {
    return this.selectedType === 'MSISDN';
  }

  public multipleSubjectTypesAvailable() {
    // currently, in Channels, we've got only one type of webhooks (per project = global)
    return false;
  }

  changeType() {
    this.webhookForm.get('types').setValue([]);
    this.refreshEventTypesToSelect();
  }

  save($event: MouseEvent) {
    $event.preventDefault();
    if (this.webhookForm.invalid) {
      markFormAsTouched(this.webhookForm);
      return;
    }
    const webhook = this.webhookForm.getRawValue() as WebhookSubscription;
    this.store.dispatch(
      this.existingId ? new UpdateWebhookAction({ id: this.existingId, webhook }) : new SaveWebhookAction(webhook)
    );
  }

  cancel() {
    this.router.navigateByUrl('/webhooks/list');
  }

  private refreshEventTypesToSelect() {
    this.eventTypesToSelect = this.allEventTypes
      .filter(type => type.subjectType === this.selectedType)
      .map(type => ({
        value: type.name,
        name: this.translate.instant(`WEBHOOKS.TYPES.${type.name}`) as string
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private handleExistingWebhook(webhook: WebhookSubscription) {
    this.existingId = webhook.id;
    updateFormGroupFromJson(this.webhookForm, webhook);
    this.refreshEventTypesToSelect();
    const types = webhook.types.map(p => p.name);
    this.webhookForm.get('types').setValue(types);
  }
}
